import { useEffect, useMemo, useRef, useState } from "react";

import {
  createTimelineStore,
  type PlaybackTransport,
  type TimelineStore,
  type VideoTimelineState,
} from "../Video";
import type { AnnotationTrackingPreviewState, TrackingPreviewFrameMetadata } from "./types";

const EMPTY_TRACKING_FRAMES: readonly TrackingPreviewFrameMetadata[] = [];

function clampPlaybackTime(seconds: number, durationSeconds: number): number {
  if (!Number.isFinite(seconds)) return 0;
  return Math.max(0, Math.min(Math.max(0, durationSeconds), seconds));
}

export interface TrackingPreviewPlayback {
  readonly active: boolean;
  /** The server-paced session is running or paused; its transport cannot seek or change rate. */
  readonly live: boolean;
  readonly frames: readonly TrackingPreviewFrameMetadata[];
  readonly latestFrame: TrackingPreviewFrameMetadata | null;
  /** Live server timestamps are exposed to the disabled shared timeline through this transport. */
  readonly transport: PlaybackTransport;
}

export function useTrackingPreviewDisplayFrame(
  playback: TrackingPreviewPlayback,
): TrackingPreviewFrameMetadata | null {
  if (!playback.active) return null;
  return playback.latestFrame ?? playback.frames.at(-1) ?? null;
}

export function useTrackingPreviewPlayback({
  preview,
  contextActive,
  mediaDurationSeconds,
  fallbackTimestampSeconds,
  onPlay,
  onPause,
}: {
  preview: AnnotationTrackingPreviewState;
  contextActive: boolean;
  mediaDurationSeconds?: number | null;
  fallbackTimestampSeconds: number;
  onPlay: () => void;
  onPause: () => void;
}): TrackingPreviewPlayback {
  const onPlayRef = useRef(onPlay);
  const onPauseRef = useRef(onPause);
  onPlayRef.current = onPlay;
  onPauseRef.current = onPause;
  const fallbackHistoryFrame = preview.frames === undefined ? preview.latestFrame : null;
  const frames = useMemo(
    () => preview.frames ?? (fallbackHistoryFrame ? [fallbackHistoryFrame] : EMPTY_TRACKING_FRAMES),
    [fallbackHistoryFrame, preview.frames],
  );
  const running = contextActive && (preview.status === "starting" || preview.status === "running");
  const paused = contextActive && preview.status === "stopped" && preview.targetMedia !== null;
  const live = running || paused;
  const active = live;
  const lastFrameTimestampSeconds = frames.at(-1)?.timestampSeconds ?? fallbackTimestampSeconds;
  const resultDurationSeconds = Math.max(
    Number.isFinite(mediaDurationSeconds) ? (mediaDurationSeconds ?? 0) : 0,
    lastFrameTimestampSeconds,
    fallbackTimestampSeconds,
  );
  const [store] = useState<TimelineStore>(() =>
    createTimelineStore({
      playing: false,
      currentTimeSeconds: 0,
      durationSeconds: 0,
      playbackRate: 1,
      status: "idle",
      capabilities: { live: false, seek: false, playbackRate: false },
    }),
  );
  useEffect(() => {
    if (!active) {
      store.setSnapshot({
        playing: false,
        currentTimeSeconds: 0,
        durationSeconds: 0,
        playbackRate: 1,
        status: "idle",
        capabilities: { live: false, seek: false, playbackRate: false },
      });
      return;
    }

    const latestTimestamp = preview.latestFrame?.timestampSeconds;
    const nextTime = clampPlaybackTime(
      Number.isFinite(latestTimestamp)
        ? (latestTimestamp ?? fallbackTimestampSeconds)
        : fallbackTimestampSeconds,
      resultDurationSeconds,
    );
    const nextPlaying = running;
    const nextStatus: VideoTimelineState["status"] = running
      ? "playing"
      : latestTimestamp === undefined
        ? "idle"
        : "paused";
    const current = store.getSnapshot();
    if (
      current.playing === nextPlaying &&
      current.currentTimeSeconds === nextTime &&
      current.durationSeconds === resultDurationSeconds &&
      current.status === nextStatus
    ) {
      return;
    }
    store.setSnapshot({
      playing: nextPlaying,
      currentTimeSeconds: nextTime,
      durationSeconds: resultDurationSeconds,
      status: nextStatus,
      capabilities: { live, seek: false, playbackRate: false },
    });
  }, [
    active,
    fallbackTimestampSeconds,
    preview.latestFrame?.timestampSeconds,
    preview.status,
    resultDurationSeconds,
    store,
    live,
    running,
  ]);

  const transport = useMemo<PlaybackTransport>(() => {
    const playLivePlayback = () => {
      if (!store.getSnapshot().playing) onPlayRef.current();
    };
    const pauseLivePlayback = () => {
      if (store.getSnapshot().playing) onPauseRef.current();
    };
    return {
      subscribe: store.subscribe,
      getSnapshot: store.getSnapshot,
      play: playLivePlayback,
      pause: pauseLivePlayback,
      toggle: () => {
        if (store.getSnapshot().playing) pauseLivePlayback();
        else playLivePlayback();
      },
      seekToSeconds: () => undefined,
      setPlaybackRate: () => undefined,
      reset: pauseLivePlayback,
    };
  }, [store]);

  return {
    active,
    live,
    frames,
    latestFrame: preview.latestFrame ?? null,
    transport,
  };
}
